import React, { FC, PropsWithChildren } from 'react'
import ko from 'knockout'
import { Navigate } from 'react-router'
import styled from '@emotion/styled'
import { ToolboxViewModel } from './viewModel'
import { useDispatch } from 'react-redux'
import { Toggle } from '@mobi/component-library/Common/Input/Toggle'
import { useAppSelector } from '@core/Store/hooks'
import { toggleKambiLogging, toggleReactQueryDevTools } from './Store'
import { VersionInfo } from './Components/VersionInfo'
import { CookieManagement } from './Components/CookieManagement'
import { LocalStorageManager } from './Components/LocalStorageManager/LocalStorageManager'
import { LastPaymentMethod } from '@core/Areas/Toolbox/Components/LastPaymentMethod/LastPaymentMethod'
import { Sentry } from '@core/Areas/Toolbox/Components/Sentry/Sentry'
import { useServerConfig } from '@core/Data/ServerConfig/useServerConfig'
import { Icon } from '@mobi/component-library/Common/V2'

export const Toolbox = () => {
  const toolboxElRef = React.useRef<HTMLDivElement>(null)
  const serverConfig = useServerConfig()
  const dispatch = useDispatch()

  const isReactQueryDevToolsEnabled = useAppSelector(
    state => state.toolbox.isReactQueryDevToolsEnabled
  )
  const isKambiLoggingEnabled = useAppSelector(state => state.toolbox.isKambiLoggingEnabled)

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  React.useEffect(() => {
    if (!serverConfig?.isInternalRequest || !toolboxElRef.current) return undefined

    const viewModel = new ToolboxViewModel()
    ko.applyBindings(viewModel, toolboxElRef.current)

    return () => {
      viewModel.cleanup()
    }
  }, [serverConfig?.isInternalRequest])

  if (!serverConfig) return null
  if (serverConfig.isInternalRequest === false) return <Navigate to='/' />

  return (
    <div ref={toolboxElRef}>
      <VersionInfo />

      <h3>
        React-Query DevTools{' '}
        <Toggle
          onToggle={() => dispatch(toggleReactQueryDevTools())}
          toggledOn={isReactQueryDevToolsEnabled}
          isDisabled={false}
        />
      </h3>

      <h3>
        Kambi console logging{' '}
        <Toggle
          onToggle={() => dispatch(toggleKambiLogging())}
          toggledOn={isKambiLoggingEnabled}
          isDisabled={false}
        />
      </h3>

      <CookieManagement />

      <ExpandableToolboxSection heading='GPS and Location' isKnockout>
        <p>
          <button
            type='button'
            data-bind='click: updateLastKnownLocation'
            className='fz-12 nue-button nue-secondary'
          >
            Get GPS Coords
          </button>
        </p>
        <p>
          GPS Coords: <span data-bind='text: lastKnownLocationText' />
          <br />
          <span data-bind='text: dateLastKnownLocationUpdated' />
        </p>
        <hr />
        <p>
          <button
            type='button'
            data-bind='click: getCurrentVenueLocation'
            className='fz-12 nue-button nue-secondary'
          >
            Get Venue/Location
          </button>
        </p>
        <p>
          Current Venue/Location: <span data-bind='text: currentVenueLocation' />
          <br />
          <span data-bind='text: currentVenueLocationUpdatedAt' />
        </p>
      </ExpandableToolboxSection>

      <ExpandableToolboxSection heading='Feature Toggles' isKnockout>
        <dl className='fz-14 pl1em' style={{ overflowY: 'scroll', maxHeight: '20rem' }}>
          <dt>
            <strong>AppCapability</strong>
          </dt>
          <dd className='ml1em'>
            <span data-bind='text: appCapability' />
          </dd>
          <dt>
            <strong>Launch Darkly Features</strong>
          </dt>
          <div data-bind='foreach: ldFeatures'>
            <dd className='ml1em'>
              <span data-bind='text: $data[0]' />:
              <span data-bind="text: $data[1], style: { color: $data[1] ? 'green' : 'red' }" />
            </dd>
          </div>
        </dl>
      </ExpandableToolboxSection>

      <ExpandableToolboxSection heading='Notifications' isKnockout>
        <dl className='fz-14 pl1em'>
          <dt>authorization:</dt>
          <dd className='ml1em'>
            <span data-bind='text: authStatus' />
          </dd>
          <dt>fcmToken:</dt>
          <dd className='ml1em'>
            <span data-bind='text: fcmToken' />
          </dd>
          <button
            type='button'
            data-bind='click: getPushAuthStatus'
            className='fz-12 nue-button nue-secondary'
          >
            Get Auth Status
          </button>
        </dl>
      </ExpandableToolboxSection>

      <LocalStorageManager />

      <LastPaymentMethod />

      <Sentry />
    </div>
  )
}

export const ExpandableToolboxSection: FC<
  { heading: string; isKnockout?: boolean } & PropsWithChildren
> = ({ heading, isKnockout = false, children }) => {
  const [isExpanded, setIsExpanded] = React.useState(false)
  return (
    <>
      <ExpandableToolboxSectionStyled onClick={() => setIsExpanded(!isExpanded)}>
        <h3>{heading}</h3>
        <div>
          <Icon name={isExpanded ? 'SolidChevronDown' : 'SolidChevronUp'} color='white' />
        </div>
      </ExpandableToolboxSectionStyled>
      <div style={{ display: isExpanded ? 'block' : isKnockout ? 'none' : 'block' }}>
        {isKnockout ? children : isExpanded ? children : null}
      </div>
    </>
  )
}

const ExpandableToolboxSectionStyled = styled.section({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: 'black',
  marginBottom: '1rem',
  padding: '0.5rem',

  '> h3': {
    margin: 0,
    padding: 0,
  },
})
